import { useNewToast } from 'hooks/newToast';
import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { toDate } from 'utils/converters';
import { appWarnings } from 'utils/appWarnings';

const success = {
  type: 'success',
  message: '',
};

const DespolpadorContext = createContext({});

export function DespolpadorProvider({ children }) {
  const { showToast } = useNewToast();

  const [despolpagens, setDespolpagens] = useState({});
  const [despolpagensLoading, setDespolpagensLoading] = useState(false);
  const [loteDespolpagem, setLoteDespolpagem] = useState({});
  const [despolpagemLoading, setDespolpagemLoading] = useState(false);

  const [lotesDespolpagens, setLotesDespolpagens] = useState([]);
  const [lotesDespolpagensTotalItems, setLotesDespolpagensTotalItems] =
    useState(0);
  const [lotesDespolpagensLoading, setLotesDespolpagensLoading] =
    useState(false);

  const [indicadoresLotesDespolpagens, setIndicadoresLotesDespolpagens] =
    useState({});
  const [
    indicadoresLotesDespolpagensLoading,
    setIndicadoresLotesDespolpagensLoading,
  ] = useState(false);

  const [saidasDespolpagens, setSaidasDespolpagens] = useState({});
  const [saidasDespolpagensLoading, setSaidasDespolpagensLoading] =
    useState(false);
  const [gerarNoDespolpadorLoading, setGerarNoDespolpadorLoading] =
    useState(false);
  const [editarNoDespolpadorLoading, setEditarNoDespolpadorLoading] =
    useState(false);
  const [deleteDespolpagemLoading, setDeleteDespolpagemLoading] =
    useState(false);
  const [deleteSaidaDespolpagemLoading, setDeleteSaidaDespolpagemLoading] =
    useState(false);
  const [deleteTahaoDespolpagemLoading, setDeleteTahaoDespolpagemLoading] =
    useState(false);

  const getDespolpagens = useCallback(async (filter) => {
    try {
      setDespolpagensLoading(true);
      const response = await api.despolpador().getDespolpagens(filter);
      setDespolpagens(response);
      setDespolpagensLoading(false);
    } catch (errors) {
      setDespolpagensLoading(false);
    }
  }, []);

  const getDespolpagem = useCallback(async (id) => {
    try {
      setDespolpagemLoading(true);
      const response = await api.despolpador().getLoteDespolpagem(id);
      setLoteDespolpagem(response);
      setDespolpagemLoading(false);
    } catch (err) {
      setDespolpagemLoading(false);
    }
  }, []);

  const getLotesDespolpagens = useCallback(async (filter) => {
    try {
      setLotesDespolpagensLoading(true);
      const response = await api.despolpador().getLotesDespolpagens(filter);
      setLotesDespolpagens(response || []);
      setLotesDespolpagensTotalItems(Number(response.total || 0));
      setLotesDespolpagensLoading(false);
    } catch (errors) {
      setLotesDespolpagensLoading(false);
      setLotesDespolpagens([]);
    }
  }, []);

  const getIndicadoresLotesDespolpagens = useCallback(async (filter) => {
    try {
      setIndicadoresLotesDespolpagensLoading(true);
      const response = await api
        .despolpador()
        .getIndicadoresLotesDespolpagens(filter);
      setIndicadoresLotesDespolpagens(response || {});
      setIndicadoresLotesDespolpagensLoading(false);
    } catch (errors) {
      setIndicadoresLotesDespolpagensLoading(false);
      setIndicadoresLotesDespolpagens({});
    }
  }, []);

  const getSaidasDespolpagens = useCallback(async (filter) => {
    try {
      setSaidasDespolpagensLoading(true);
      const response = await api.despolpador().getSaidasDespolpagens(filter);
      setSaidasDespolpagens(response);
      setSaidasDespolpagensLoading(false);
    } catch (errors) {
      setSaidasDespolpagensLoading(false);
    }
  }, []);

  const getColhidoViagem = useCallback(async (data, viagem) => {
    const response = await api.despolpador().getColhidoViagem(data, viagem);
    return response?.data ?? response;
  }, []);

  const getColhidoTalhao = useCallback(async (data, talhao) => {
    const response = await api.despolpador().getColhidoTalhao(data, talhao);
    return response?.data ?? response;
  }, []);

  const gerarNoDespolpador = useCallback(
    async (dados) => {
      try {
        setGerarNoDespolpadorLoading(true);
        const {
          data,
          responsavel,
          obs,
          lavado = false,
          despolpado = false,
          descascado = false,
        } = dados.dados;
        const { arrayCafe, arrayTalhoes, arrayViagens, tipoEntrada } = dados;

        const isPorViagem = tipoEntrada === 'viagem';

        const talhaoPayload =
          arrayTalhoes
            ?.filter((item) => !!item && item.talhao?.id)
            ?.map((item) => ({
              talhao: item.talhao.id,
              data: toDate(item.colheita),
              quantidade:
                item.colhido != null && !Number.isNaN(Number(item.colhido))
                  ? Number(item.colhido)
                  : 0,
            })) ?? [];

        const viagemPayload =
          arrayViagens
            ?.filter((item) => !!item)
            ?.map((item) => ({
              numeroViagem:
                item.numeroViagem != null && item.numeroViagem !== ''
                  ? Number(item.numeroViagem) || String(item.numeroViagem)
                  : '',
              data: toDate(item.data),
              quantidade:
                item.quantidade != null &&
                !Number.isNaN(Number(item.quantidade))
                  ? Number(item.quantidade)
                  : null,
            })) ?? [];

        const newData = {
          data: toDate(data),
          descricao: obs,
          responsavel,
          lavado: !!lavado,
          despolpado: !!despolpado,
          descascado: !!descascado,
          talhao: isPorViagem ? [] : talhaoPayload,
          viagem: isPorViagem ? viagemPayload : [],
          saida:
            arrayCafe
              ?.filter((item) => !!item && item.tipo?.nome)
              ?.map((item) => {
                const medidas =
                  item.medidas != null &&
                  item.medidas !== '' &&
                  !Number.isNaN(Number(item.medidas))
                    ? Number(item.medidas)
                    : null;
                const porcentagem =
                  item.porcentagem != null &&
                  item.porcentagem !== '' &&
                  !Number.isNaN(Number(item.porcentagem))
                    ? Number(item.porcentagem)
                    : null;
                const quantidade = item.quantidade ?? '';
                return {
                  tipo: item.tipo?.nome,
                  quantidade:
                    typeof quantidade === 'number'
                      ? quantidade
                      : String(quantidade),
                  ...(medidas != null && { medidas }),
                  ...(porcentagem != null && { porcentagem }),
                };
              }) ?? [],
        };
        const response = await api.despolpador().gerarNoDespolpador(newData);
        showToast({
          ...success,
          message: 'Entrada no descascador gerada com sucesso!',
        });
        setGerarNoDespolpadorLoading(false);

        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        setGerarNoDespolpadorLoading(false);
        throw new Error(err);
      }
    },
    [showToast],
  );

  const editarNoDespolpador = useCallback(
    async (id, dados) => {
      try {
        setEditarNoDespolpadorLoading(true);
        const {
          data,
          responsavel,
          obs,
          lavado = false,
          despolpado = false,
          descascado = false,
        } = dados.dados;
        const { arrayCafe, arrayTalhoes, arrayViagens, tipoEntrada } = dados;
        const tipo = tipoEntrada ?? 'talhao';

        const talhaoPayload =
          tipo === 'talhao' && arrayTalhoes
            ? arrayTalhoes
                .filter((item) => !!item && item.talhao?.id)
                .map((item) => ({
                  ...(item.idTalhao && { id: item.idTalhao }),
                  talhao: item.talhao.id,
                  data: toDate(item.colheita),
                  quantidade:
                    item.colhido != null && !Number.isNaN(Number(item.colhido))
                      ? Number(item.colhido)
                      : 0,
                }))
            : [];

        const viagemPayload =
          tipo === 'viagem' && arrayViagens
            ? arrayViagens
                .filter((item) => !!item)
                .map((item) => {
                  const payload = {
                    numeroViagem:
                      item.numeroViagem != null && item.numeroViagem !== ''
                        ? Number(item.numeroViagem) || String(item.numeroViagem)
                        : '',
                    data: toDate(item.data),
                    quantidade:
                      item.quantidade != null &&
                      !Number.isNaN(Number(item.quantidade))
                        ? Number(item.quantidade)
                        : null,
                  };
                  if (item.id) payload.id = item.id;
                  return payload;
                })
            : [];

        const newData = {
          data: toDate(data),
          descricao: obs,
          responsavel,
          lavado: !!lavado,
          despolpado: !!despolpado,
          descascado: !!descascado,
          talhao: talhaoPayload,
          viagem: viagemPayload,
          saida:
            arrayCafe
              ?.filter((item) => !!item && item.tipo?.nome)
              ?.map((item) => {
                const medidas =
                  item.medidas != null &&
                  item.medidas !== '' &&
                  !Number.isNaN(Number(item.medidas))
                    ? Number(item.medidas)
                    : null;
                const porcentagem =
                  item.porcentagem != null &&
                  item.porcentagem !== '' &&
                  !Number.isNaN(Number(item.porcentagem))
                    ? Number(item.porcentagem)
                    : null;
                const quantidade = item.quantidade ?? '';
                return {
                  ...(item.idLote && { id: item.idLote }),
                  tipo: item.tipo?.nome,
                  quantidade:
                    typeof quantidade === 'number'
                      ? quantidade
                      : String(quantidade),
                  ...(medidas != null && { medidas }),
                  ...(porcentagem != null && { porcentagem }),
                };
              }) ?? [],
        };
        const response = await api
          .despolpador()
          .editarNoDespolpador(id, newData);
        showToast({
          ...success,
          message: 'Entrada no descascador editada com sucesso!',
        });
        setEditarNoDespolpadorLoading(false);
        return response;
      } catch (err) {
        setEditarNoDespolpadorLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deleteDespolpagem = useCallback(
    async (id) => {
      try {
        setDeleteDespolpagemLoading(true);
        await api.despolpador().deleteDespolpagem(id);
        showToast({
          ...success,
          message: 'Descascagem removida com sucesso!',
        });
        setDeleteDespolpagemLoading(false);
      } catch (err) {
        setDeleteDespolpagemLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deleteSaidaDespolpagem = useCallback(
    async (id) => {
      try {
        setDeleteSaidaDespolpagemLoading(true);
        await api.despolpador().removeSaidaDespolpagem(id);
        setLoteDespolpagem({
          ...loteDespolpagem,
          saidas: loteDespolpagem.saidas.filter((item) => item.id !== id),
        });
        showToast({ ...success, message: 'Saída removida com sucesso' });
        setDeleteSaidaDespolpagemLoading(false);
      } catch (error) {
        showToast(appWarnings.Danger(error));
        setDeleteSaidaDespolpagemLoading(false);
        throw new Error(error);
      }
    },
    [loteDespolpagem, showToast],
  );

  const deleteTahaoDespolpagem = useCallback(
    async (id) => {
      try {
        setDeleteTahaoDespolpagemLoading(true);
        await api.despolpador().removeColhidoDespolpagem(id);
        setLoteDespolpagem({
          ...loteDespolpagem,
          entradas: loteDespolpagem.entradas.filter((item) => item.id !== id),
        });
        showToast({ ...success, message: 'Entrada removida com sucesso' });
        setDeleteTahaoDespolpagemLoading(false);
      } catch (error) {
        showToast(appWarnings.Danger(error));
        setDeleteTahaoDespolpagemLoading(false);
        throw new Error(error);
      }
    },
    [loteDespolpagem, showToast],
  );

  return (
    <DespolpadorContext.Provider
      value={useMemo(
        () => ({
          getDespolpagens,
          getLotesDespolpagens,
          getIndicadoresLotesDespolpagens,
          getSaidasDespolpagens,
          despolpagens,
          deleteSaidaDespolpagem,
          deleteSaidaDespolpagemLoading,
          deleteTahaoDespolpagemLoading,
          deleteTahaoDespolpagem,
          lotesDespolpagens,
          indicadoresLotesDespolpagens,
          indicadoresLotesDespolpagensLoading,
          saidasDespolpagens,
          deleteDespolpagem,
          despolpagensLoading,
          lotesDespolpagensLoading,
          saidasDespolpagensLoading,
          deleteDespolpagemLoading,
          gerarNoDespolpador,
          gerarNoDespolpadorLoading,
          editarNoDespolpador,
          editarNoDespolpadorLoading,
          getDespolpagem,
          despolpagemLoading,
          loteDespolpagem,
          lotesDespolpagensTotalItems,
          getColhidoViagem,
          getColhidoTalhao,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- funções do provider estáveis
        [
          getDespolpagens,
          getLotesDespolpagens,
          getIndicadoresLotesDespolpagens,
          getSaidasDespolpagens,
          despolpagens,
          deleteSaidaDespolpagem,
          deleteSaidaDespolpagemLoading,
          deleteTahaoDespolpagemLoading,
          deleteTahaoDespolpagem,
          lotesDespolpagens,
          indicadoresLotesDespolpagens,
          indicadoresLotesDespolpagensLoading,
          saidasDespolpagens,
          deleteDespolpagem,
          despolpagensLoading,
          lotesDespolpagensLoading,
          saidasDespolpagensLoading,
          deleteDespolpagemLoading,
          gerarNoDespolpador,
          gerarNoDespolpadorLoading,
          editarNoDespolpador,
          editarNoDespolpadorLoading,
          getDespolpagem,
          despolpagemLoading,
          loteDespolpagem,
          lotesDespolpagensTotalItems,
          getColhidoViagem,
          getColhidoTalhao,
        ],
      )}
    >
      {children}
    </DespolpadorContext.Provider>
  );
}

export function useDespolpador() {
  const context = useContext(DespolpadorContext);

  if (!context)
    throw new Error('useDespolpador must be used within an AuthProvider');

  return context;
}
